import { Workbox } from 'workbox-window';
import updateStore from '@/stores/updateStore';
import { trace, redactUrl } from '@/utils/authTrace';
import { readWatchdogState, shouldForceUpdate, markUpdateFired, isCallbackLanding } from '@/utils/swWatchdog';

// Phase-A1 (2026-08-04) — [Z-C]'s 24h staleness bound: update check on
// launch AND hourly while open, reload prompt rather than a silent swap.
// Only registers in production — in dev, vue-cli-service's own dev server
// already serves fresh assets on every request, and a dev-registered SW
// would fight its hot-reload (a stale precached bundle winning a race
// against webpack's live one is a real, previously-reported class of bug
// in other Workbox setups, not a theoretical concern).
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // hourly

// Phase 2 (2026-08-26) — INTENT-BASED reload discriminator, replacing the
// hadController proxy that gated this before.
//
// Why replace rather than augment. hadController asked "did a controller exist
// when this page loaded?" as a stand-in for "did the user ask for this update?".
// It answered wrong whenever the page loaded uncontrolled — hard refresh, the
// first visit in which the SW registered, or iOS standalone with a fresh SW
// partition — so the click posted skipWaiting, the worker activated, and the
// reload was then silently skipped. That was the whole bug.
//
// service-worker.js's skipWaiting() is reachable from exactly one path: the
// SKIP_WAITING message this file posts on the user's click. So while the page
// is open, a new worker can only take over because the user clicked, and
// intent is directly observable instead of inferred.
//
// This is STRICTLY STRONGER for the Auth0 defect hadController was added to
// close (Z-campaign Phase 1, Defect A — a reload firing mid redirect-callback
// on standalone iOS). hadController still permitted a reload on any
// controllerchange a CONTROLLED page happened to catch, including during a
// callback landing. updateClicked cannot be true without a click, so no
// unclicked controllerchange can reload the page at all.
//
// hadController itself is KEPT, but only as a trace breadcrumb — it gates
// nothing now. It is the diagnostic the iOS standalone investigation reads,
// and the verification of this very change depends on being able to see it.
let updateClicked = false;
let reloadFired = false;

// If no controllerchange arrives, the click leaves the user with a spinner and
// nothing else. Force the reload so there is always an outcome.
const RELOAD_TIMEOUT_MS = 3000;

// The ONLY place this file calls window.location.reload(). The module-scope
// reloadFired guard is what makes a repeat controllerchange — or a
// controllerchange racing the timeout — unable to reload a second time. A
// reload loop here would strand every user, so the guard is deliberately not
// per-invocation state.
function reloadOnce(reason) {
  if (reloadFired) return false;
  reloadFired = true;
  // Synchronous localStorage write, so this breadcrumb survives the reload it
  // is timestamping — a console log would be wiped with the page.
  trace('sw-reload-firing', { reason });
  window.location.reload();
  return true;
}

export function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return;
  }

  // Z-campaign Phase 1, Defect A (2026-08-09) — captured BEFORE wb.register()
  // and held in this closure var (not re-read later), since the whole point
  // is comparing "did a controller exist before this registration ran" —
  // reading navigator.serviceWorker.controller again inside the 'controlling'
  // handler below would just report the NEW controller that event itself is
  // announcing, always true, useless as a signal.
  const hadController = !!navigator.serviceWorker.controller;
  trace('sw-registering', { controllerExistedBeforeRegister: hadController });
  const wb = new Workbox(`${process.env.BASE_URL}service-worker.js`);

  // Fires when a new SW has finished installing and is sitting in "waiting"
  // state (an update exists and is ready, but hasn't taken over yet — see
  // service-worker.js for why it waits). This is the ONLY place the reload
  // prompt gets shown; nothing here reloads the page on its own.
  wb.addEventListener('waiting', () => {
    trace('sw-waiting');
    updateStore.setUpdateAvailable(() => {
      // Intent is recorded BEFORE the message is posted: skipWaiting can take
      // effect fast enough that 'controlling' fires before this callback
      // returns, and a reload gated on a flag set afterwards would miss it.
      updateClicked = true;
      trace('sw-update-clicked');
      // wb.messageSkipWaiting() posts exactly the {type:'SKIP_WAITING'}
      // message service-worker.js's own listener acts on.
      wb.messageSkipWaiting();
      // Fallback, not the happy path — 'controlling' normally wins this race
      // and reloadOnce() then makes this call a no-op.
      setTimeout(() => {
        if (reloadOnce('timeout-no-controllerchange')) {
          trace('sw-reload-timeout-fallback');
        }
      }, RELOAD_TIMEOUT_MS);
    });
  });

  // Fires once the new SW has actually taken control (after skipWaiting +
  // clientsClaim have both run) — this is what makes the reload actually
  // pick up the new JS/CSS bundle, not just a new SW registration.
  //
  // Dispatch Brief v2, Phase 2 (2026-08-05), Addendum item 1 identified that
  // this ALSO fires on a brand-new registration with no prior controller
  // (see service-worker.js's clientsClaim() comment): 'controlling' does not
  // by itself distinguish "an update just took over" from "this is the very
  // first SW registration in this storage partition, claiming an
  // uncontrolled page." That was the leading hypothesis for the reload()
  // firing mid-flight during an Auth0 redirect-callback landing, on
  // standalone iOS where a WKWebView's SW partition can be genuinely fresh
  // on every app relaunch (fresh partition = first install every time).
  //
  // Z-campaign Phase 1, Defect A (2026-08-09) was fixed here by gating the
  // reload on hadController. Phase 2 (2026-08-26) REPLACED that gate with
  // updateClicked — see the module-scope comment for the full reasoning. The
  // defect stays closed and is in fact closed harder: a first-install claim on
  // a fresh iOS partition carries no click, so it cannot reload, and neither
  // can any other controllerchange the page catches mid-callback-landing.
  //
  // trace() here is a synchronous localStorage write specifically so this
  // breadcrumb survives the reload it is timestamping (when one happens) — a
  // console-only log would be wiped with the page.
  wb.addEventListener('controlling', () => {
    trace('sw-controlling', {
      url: redactUrl(window.location.href),
      hasCode: window.location.search.includes('code='),
      // Kept purely as a breadcrumb — it gates nothing. See the module-scope
      // comment above for why the gate is updateClicked instead.
      hadController,
      updateClicked,
    });
    if (updateClicked) {
      reloadOnce('controllerchange');
    }
  });

  wb.register().then((registration) => {
    if (!registration) return;
    trace('sw-registered', { scope: registration.scope });

    // Update check on launch: register() itself already triggers an
    // initial check, but a registration that was already installed from a
    // previous visit (the common case, not a fresh install) doesn't
    // automatically re-check on this launch without an explicit call.
    registration.update();

    // Update check on interval while open — the 24h staleness bound is
    // enforced by checking far more often than that (hourly), not by
    // trusting a single boot-time check to catch every possible update
    // during a long session.
    setInterval(() => {
      registration.update();
    }, UPDATE_CHECK_INTERVAL_MS);

    // Z-campaign Phase 1, Defect B (2026-08-09) — dead-man's-switch: a
    // repeatedly-dying app can never complete the three-gate update flow
    // (wb 'waiting' -> user's reload click -> skipWaiting -> 'controlling'),
    // so it would otherwise re-serve the same broken precached shell
    // forever. main.js's recordBoot() (debug-independent, see
    // swWatchdog.js) has already counted this boot; if that puts us at 3+
    // boots inside the window with no settled ("boot healthy") landing
    // since, force an update check and, if a version is already sitting in
    // 'waiting', skip straight to it — no reload of our own either way,
    // this only pushes a new version into place for whenever the user (or
    // the next natural reload) picks it up. One attempt per burst
    // (REFIRE_COOLDOWN_MS), then stands down.
    // Never fire mid-callback-landing (?code=&state=) — forcing an update
    // here would race registration.update()/skipWaiting against the Auth0
    // code exchange and handleUserLogin's org lookup, the exact class of
    // timing bug this campaign spent its first two phases fixing. Skipping
    // here costs nothing: recordBoot() has already counted this boot, so a
    // genuinely wedged app still trips the switch on its NEXT boot instead.
    const watchdogState = readWatchdogState();
    if (isCallbackLanding(window.location.search)) {
      trace('sw-watchdog-skipped-callback-landing', watchdogState);
    } else if (shouldForceUpdate({ ...watchdogState, now: Date.now() })) {
      trace('sw-watchdog-forcing-update', watchdogState);
      markUpdateFired();
      registration.update();
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    }
  });
}
